import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { collection, query, where, orderBy, onSnapshot, updateDoc, doc, deleteDoc, writeBatch } from 'firebase/firestore';
import { db } from '../firebase';
import { Bell, CheckCircle, Trash2, Search, Filter, ChevronDown, Check, X, Calendar, ArrowRight } from 'lucide-react';
import { markAsRead, NOTIFICATION_TYPES } from '../utils/notificationUtils';

const TYPE_COLORS = {
  SHIFT_ASSIGNED: 'bg-blue-500/20 text-blue-400',
  SHIFT_UPDATED: 'bg-indigo-500/20 text-indigo-400',
  SHIFT_CANCELLED: 'bg-red-500/20 text-red-400',
  ROTA_PUBLISHED: 'bg-green-500/20 text-green-400',
  SWAP_REQUEST: 'bg-amber-500/20 text-amber-400',
  SWAP_APPROVED: 'bg-emerald-500/20 text-emerald-400',
  SWAP_REJECTED: 'bg-red-500/20 text-red-400',
  LEAVE_APPROVED: 'bg-teal-500/20 text-teal-400',
  LEAVE_REJECTED: 'bg-rose-500/20 text-rose-400',
  SHIFT_REMINDER: 'bg-cyan-500/20 text-cyan-400',
  COVERAGE_GAP: 'bg-orange-500/20 text-orange-400',
  AI_SUGGESTION: 'bg-purple-500/20 text-purple-400',
};

const TYPE_ICONS = {
  SHIFT_ASSIGNED: '📋',
  SHIFT_UPDATED: '✏️',
  SHIFT_CANCELLED: '❌',
  ROTA_PUBLISHED: '📅',
  SWAP_REQUEST: '🔄',
  SWAP_APPROVED: '✅',
  SWAP_REJECTED: '🚫',
  LEAVE_APPROVED: '🏖️',
  LEAVE_REJECTED: '🚫',
  SHIFT_REMINDER: '⏰',
  COVERAGE_GAP: '⚠️',
  AI_SUGGESTION: '🤖',
};

export default function NotificationsCentre({ user, userData, companyId }) {
  const { t } = useTranslation();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState('all');
  const [filterRead, setFilterRead] = useState('all'); // all, unread, read
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedIds, setSelectedIds] = useState(new Set());

  useEffect(() => {
    if (!companyId || !user?.uid) return;
    const q = query(
      collection(db, 'companies', companyId, 'shiftsNotifications'),
      where('userId', '==', user.uid),
      orderBy('createdAt', 'desc')
    );
    const unsub = onSnapshot(q, snap => {
      setNotifications(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    }, () => setLoading(false));
    return unsub;
  }, [companyId, user?.uid]);

  const filtered = useMemo(() => {
    let list = notifications;
    if (filterType !== 'all') list = list.filter(n => n.type === filterType);
    if (filterRead === 'unread') list = list.filter(n => !n.read);
    if (filterRead === 'read') list = list.filter(n => n.read);
    if (searchTerm) list = list.filter(n =>
      (n.title || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (n.message || '').toLowerCase().includes(searchTerm.toLowerCase())
    );
    return list;
  }, [notifications, filterType, filterRead, searchTerm]);

  const unreadCount = notifications.filter(n => !n.read).length;

  const handleMarkRead = async (id) => {
    try { await markAsRead(companyId, id); } catch {}
  };

  const handleMarkAllRead = async () => {
    const batch = writeBatch(db);
    notifications.filter(n => !n.read).forEach(n => {
      batch.update(doc(db, 'companies', companyId, 'shiftsNotifications', n.id), {
        read: true,
        readAt: new Date().toISOString()
      });
    });
    try { await batch.commit(); } catch {}
  };

  const handleDelete = async (id) => {
    try { await deleteDoc(doc(db, 'companies', companyId, 'shiftsNotifications', id)); } catch {}
  };

  const handleBulkDelete = async () => {
    if (!window.confirm(t('notifications.confirmDelete', `Delete ${selectedIds.size} notifications?`))) return;
    const batch = writeBatch(db);
    selectedIds.forEach(id => {
      batch.delete(doc(db, 'companies', companyId, 'shiftsNotifications', id));
    });
    try { await batch.commit(); setSelectedIds(new Set()); } catch {}
  };

  const toggleSelect = (id) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelectedIds(next);
  };

  const selectAll = () => {
    if (selectedIds.size === filtered.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(filtered.map(n => n.id)));
  };

  const typeOptions = [
    { value: 'all', label: t('notifications.allTypes', 'All Types') },
    ...Object.keys(NOTIFICATION_TYPES).map(k => ({
      value: k,
      label: k.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
    }))
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Bell className="text-primary" size={24} />
            {t('notifications.centre', 'Notification Centre')}
          </h1>
          <p className="text-sm text-gray-400 mt-1">
            {unreadCount > 0
              ? t('notifications.unreadCount', `${unreadCount} unread notification${unreadCount > 1 ? 's' : ''}`)
              : t('notifications.allRead', 'All caught up!')}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {unreadCount > 0 && (
            <button onClick={handleMarkAllRead}
              className="flex items-center gap-1.5 px-3 py-2 bg-green-600/20 hover:bg-green-600/30 text-green-400 rounded-lg text-sm font-medium transition">
              <CheckCircle size={14} /> {t('notifications.markAllRead', 'Mark All Read')}
            </button>
          )}
          {selectedIds.size > 0 && (
            <button onClick={handleBulkDelete}
              className="flex items-center gap-1.5 px-3 py-2 bg-red-600/20 hover:bg-red-600/30 text-red-400 rounded-lg text-sm font-medium transition">
              <Trash2 size={14} /> {t('notifications.deleteSelected', `Delete (${selectedIds.size})`)}
            </button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
            placeholder={t('notifications.search', 'Search notifications...')}
            className="w-full pl-10 pr-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm text-white placeholder-gray-400 focus:ring-2 focus:ring-primary focus:border-transparent" />
        </div>
        <select value={filterType} onChange={e => setFilterType(e.target.value)}
          className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:ring-2 focus:ring-primary [color-scheme:dark]">
          {typeOptions.map(o => (
            <option key={o.value} value={o.value} className="bg-slate-700">{o.label}</option>
          ))}
        </select>
        <div className="flex bg-white/5 rounded-lg border border-white/10 overflow-hidden">
          {['all', 'unread', 'read'].map(f => (
            <button key={f} onClick={() => setFilterRead(f)}
              className={`px-3 py-2 text-sm font-medium transition ${filterRead === f ? 'bg-primary text-white' : 'text-gray-400 hover:text-white'}`}>
              {f === 'all' ? t('common.all', 'All') : f === 'unread' ? t('notifications.unread', 'Unread') : t('notifications.read', 'Read')}
            </button>
          ))}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="bg-white/5 border border-white/10 rounded-xl p-3 text-center">
          <p className="text-lg font-bold text-white">{notifications.length}</p>
          <p className="text-xs text-gray-400">{t('notifications.total', 'Total')}</p>
        </div>
        <div className="bg-white/5 border border-white/10 rounded-xl p-3 text-center">
          <p className="text-lg font-bold text-primary">{unreadCount}</p>
          <p className="text-xs text-gray-400">{t('notifications.unread', 'Unread')}</p>
        </div>
        <div className="bg-white/5 border border-white/10 rounded-xl p-3 text-center">
          <p className="text-lg font-bold text-green-400">{notifications.filter(n => n.read).length}</p>
          <p className="text-xs text-gray-400">{t('notifications.read', 'Read')}</p>
        </div>
        <div className="bg-white/5 border border-white/10 rounded-xl p-3 text-center">
          <p className="text-lg font-bold text-gray-400">{filtered.length}</p>
          <p className="text-xs text-gray-400">{t('notifications.showing', 'Showing')}</p>
        </div>
      </div>

      {/* Select all checkbox */}
      {filtered.length > 0 && (
        <div className="flex items-center gap-2">
          <input type="checkbox" checked={selectedIds.size === filtered.length && filtered.length > 0}
            onChange={selectAll} className="rounded accent-primary" />
          <span className="text-xs text-gray-400">{t('notifications.selectAll', 'Select All')}</span>
        </div>
      )}

      {/* Notification List */}
      <div className="space-y-2">
        {filtered.length === 0 ? (
          <div className="text-center py-16 text-gray-400">
            <Bell size={40} className="mx-auto mb-3 opacity-30" />
            <p className="text-lg font-medium">{t('notifications.noneFound', 'No notifications found')}</p>
            <p className="text-sm mt-1">{t('notifications.tryFilter', 'Try adjusting your filters')}</p>
          </div>
        ) : (
          filtered.map(n => (
            <div key={n.id}
              className={`flex items-start gap-3 p-4 rounded-xl transition group ${
                !n.read ? 'bg-primary/5 border border-primary/20' : 'bg-white/5 border border-white/10'
              } hover:bg-white/10`}
            >
              <input type="checkbox" checked={selectedIds.has(n.id)}
                onChange={() => toggleSelect(n.id)} className="mt-1 rounded accent-primary flex-shrink-0" />

              <div className="flex-shrink-0 mt-0.5 text-lg">{TYPE_ICONS[n.type] || '📌'}</div>

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${TYPE_COLORS[n.type] || 'bg-gray-500/20 text-gray-400'}`}>
                    {(n.type || 'NOTIFICATION').replace(/_/g, ' ')}
                  </span>
                  {!n.read && <span className="w-2 h-2 bg-primary rounded-full" />}
                  <span className="text-[10px] text-gray-500 ml-auto">{formatTimeAgo(n.createdAt)}</span>
                </div>
                <p className={`text-sm font-medium ${!n.read ? 'text-white' : 'text-gray-300'}`}>{n.title}</p>
                <p className="text-xs text-gray-400 mt-0.5">{n.message}</p>
                {(n.shiftId || n.rotaId || n.swapRequestId) && (
                  <div className="flex gap-2 mt-2">
                    {n.shiftId && <span className="text-[10px] bg-white/5 px-1.5 py-0.5 rounded text-gray-400">Shift: {n.shiftId.slice(0, 8)}</span>}
                    {n.swapRequestId && <span className="text-[10px] bg-white/5 px-1.5 py-0.5 rounded text-gray-400">Swap: {n.swapRequestId.slice(0, 8)}</span>}
                  </div>
                )}
              </div>

              <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition flex-shrink-0">
                {!n.read && (
                  <button onClick={() => handleMarkRead(n.id)} title="Mark as read"
                    className="p-1.5 rounded-lg hover:bg-green-500/20 text-gray-400 hover:text-green-400 transition">
                    <Check size={14} />
                  </button>
                )}
                <button onClick={() => handleDelete(n.id)} title="Delete"
                  className="p-1.5 rounded-lg hover:bg-red-500/20 text-gray-400 hover:text-red-400 transition">
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function formatTimeAgo(dateStr) {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now - date;
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return 'Just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return `${diffH}h ago`;
  const diffD = Math.floor(diffH / 24);
  if (diffD < 7) return `${diffD}d ago`;
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}
